import React, { useState, useEffect } from 'react';
import axios from "axios";
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTachometerAlt,
  faFileInvoice,
  faCoins,
  faRotateLeft,
  faFileInvoiceDollar,
  faBan,
  faFileCircleXmark,
} from '@fortawesome/free-solid-svg-icons';
import NavTab from '../components/NavTab/NavTab';
import OrderInformation from '../components/Order/OrderInformation';
import OrderOverview from '../components/Order/OrderOverview';
import OrderInvoices from '../components/Order/OrderInvoices';
import OrderReturns from '../components/Order/OrderReturns';
import OrderPayments from '../components/Order/OrderPayments';
import InlineLoader from '../components/Loader/InlineLoader';
import { useBreadcrumb } from '../layouts/BreadcrumbContext';
import FabGroup from '../components/FabGroup';
import NewInvoiceModal from '../components/Modal/NewInvoiceModal';
import CreateReturnModal from '../components/Modal/CreateReturnModal';
import ConfirmModal from '../components/Modal/ConfirmModal';
import { useAuth } from '../context/AuthProvider';
import useRecordNeighbors from '../hooks/useRecordNeighbors';
import { API_BASE_URL } from '../api';

// order sheet panels: brand-blue title icons, like the product sheet's
const SECTION_ICON_STYLE = { background: '#eff6ff', color: '#2563eb' };

const TAB_SLUGS = {
  orderInvoices: 'invoices',
  orderPayments: 'payments',
  orderReturns: 'returns',
};

const SLUG_TO_TAB = Object.fromEntries(
  Object.entries(TAB_SLUGS).map(([tabId, slug]) => [slug, tabId])
);

const OrderDetails = () => {
  const { t } = useTranslation();
  const { id, tab } = useParams();
  const navigate = useNavigate();
  const { hasRight } = useAuth();
  const canViewPayments = hasRight('PAYMENTS_VIEW');
  // The FAB below creates an Invoice record that also carries the payment method/amount
  // (this app's "encaisser" flow) — it POSTs to /orders/{id}/invoices, so both rights
  // are required to see it.
  const canCreateInvoicePayment = hasRight('INVOICES_CREATE') && hasRight('PAYMENTS_CREATE');
  const canCancel = hasRight('ORDERS_CANCEL');
  const canCreateReturn = hasRight('RETURNS_CREATE');
  const canViewReturns = hasRight('RETURNS_VIEW');
  const [order, setOrder] = useState(null);
  const [returns, setReturns] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showReturnModal, setShowReturnModal]   = useState(false);
  const [showCancelModal, setShowCancelModal]   = useState(false);
  const { setTitle } = usePageTitle();
  const { setBreadcrumb } = useBreadcrumb();

  const fetchOrder = React.useCallback(async () => {
    try {
      setLoading(true);
      const orderRes = await axios.get(`${API_BASE_URL}/api/v1/getOrder/${id}`, { withCredentials: true });
      setOrder(orderRes.data);
      setTitle(orderRes.data.reference);
      document.title = orderRes.data.reference;
      setBreadcrumb([
        { label: t('Orders'), url: '/orders' },
        { label: orderRes.data.reference }
      ]);

      if (canViewReturns) {
        axios.get(`${API_BASE_URL}/api/v1/returns/by-order/${id}`, { withCredentials: true })
          .then(r => setReturns(r.data))
          .catch(() => {});
      }

      if (canViewPayments) {
        axios.get(`${API_BASE_URL}/api/v1/payments/by-order/${id}`, { withCredentials: true })
          .then(r => setPayments(r.data))
          .catch(() => {});
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id, t, canViewPayments, canViewReturns]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  // Previous / next sale (header arrows + ← → keys) — keeps the current tab (factures,
  // paiements…) when moving to another sale
  const orderPath = React.useCallback((targetId) => `/order/${targetId}${tab ? `/${tab}` : ''}`, [tab]);
  const { neighbors, goTo: goToOrder } = useRecordNeighbors(`/api/v1/orders/${id}/neighbors`, orderPath);

  if (loading) {
    return <InlineLoader loadingMessage={t('Loading...')} />;
  }

  if (!order) {
    return (
      <div className="page-content">
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          minHeight: '60vh', textAlign: 'center', gap: 16,
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: '#fef2f2', color: '#ef4444',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28,
          }}>
            <FontAwesomeIcon icon={faFileCircleXmark} />
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#111827' }}>{t('order_details.not_found_title')}</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
              {t('order_details.not_found_desc')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const panel = (title, icon, children) => (
    <div className="order-info-wrapper">
      <div className="order-section-card">
        <div className="order-section-title">
          <span className="order-section-title-main">
            <span className="order-section-icon" style={SECTION_ICON_STYLE}>
              <FontAwesomeIcon icon={icon} />
            </span>
            {title}
          </span>
        </div>
        {children}
      </div>
    </div>
  );

  const handleTabChange = (tabId) => {
    const slug = TAB_SLUGS[tabId];
    navigate(slug ? `/order/${id}/${slug}` : `/order/${id}`);
  };

  const menuItems = [
    { id: 'orderOverview', icon: faTachometerAlt, label: t('order_details.tab.overview'), component: <OrderOverview order={order} onRefresh={fetchOrder} /> },
    { id: 'orderInvoices', icon: faFileInvoice, label: order.invoices?.length > 0 ? t('order_details.tab.invoices_count', { count: order.invoices.length }) : t('Invoices'), component: panel(t('Invoices'), faFileInvoice, <OrderInvoices invoices={order.invoices} />) },
    ...(canViewPayments ? [{ id: 'orderPayments', icon: faCoins, label: payments.length > 0 ? t('order_details.tab.payments_count', { count: payments.length }) : t('Payments'), component: panel(t('Payments'), faCoins, <OrderPayments payments={payments} />) }] : []),
    ...(canViewReturns ? [{ id: 'orderReturns', icon: faRotateLeft, label: returns.length > 0 ? t('order_details.tab.returns_count', { count: returns.length }) : t('Returns'), component: panel(t('Returns'), faRotateLeft, <OrderReturns returns={returns} />) }] : []),
  ];

  const handleConfirmCancel = () => {
    axios.patch(
      `${API_BASE_URL}/api/v1/orders/${id}/status`,
      { status: 'CANCELED' },
      { withCredentials: true }
    ).then(() => {
      setShowCancelModal(false);
      fetchOrder();
    }).catch(err => console.error('Failed to cancel order:', err));
  };

  const requestedTab = SLUG_TO_TAB[tab];
  const activeTab = menuItems.some(item => item.id === requestedTab) ? requestedTab : 'orderOverview';

  const fabButtons = [
    ...(order.status === 'PENDING' && canCreateInvoicePayment ? [{
      id: 'invoice',
      label: t('order_details.action.new_invoice'),
      icon: faFileInvoiceDollar,
      onClick: () => setShowInvoiceModal(true),
    }] : []),
    ...(canCreateReturn ? [{
      id: 'return',
      label: t('Create return'),
      icon: faRotateLeft,
      onClick: () => setShowReturnModal(true),
    }] : []),
    ...(order.status !== 'CANCELED' && canCancel ? [{
      id: 'cancel',
      label: t('Cancel order'),
      icon: faBan,
      onClick: () => setShowCancelModal(true),
    }] : []),
  ];

  return (
    <div className="page-content">
      <OrderInformation order={order} neighbors={neighbors} onNavigate={goToOrder} />

      <NavTab menuItems={menuItems} activeTab={activeTab} onTabChange={handleTabChange} className="navtab--narrow" />

      <FabGroup buttons={fabButtons} />

      <NewInvoiceModal
        order={order}
        show={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        onSuccess={fetchOrder}
      />

      <CreateReturnModal
        order={order}
        show={showReturnModal}
        onClose={() => setShowReturnModal(false)}
        onSave={fetchOrder}
      />

      <ConfirmModal
        id="cancelOrderModal"
        title={t('order_details.cancel_modal.title')}
        description={t('order_details.cancel_modal.description')}
        confirmLabel={t('order_details.cancel_modal.confirm')}
        cancelLabel={t('order_details.cancel_modal.cancel')}
        show={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleConfirmCancel}
      />
    </div>
  );
};

export default OrderDetails;
